/**
 * Accrochage de la position sur les traces GPX et le réseau routier.
 *
 * Portage de `SnapToRoad.kt`. Le GPS seul place le curseur à côté de la route ;
 * l'accrochage le ramène sur la géométrie et fournit surtout une **abscisse
 * curviligne**, sans laquelle ni l'indicateur kilométrique, ni le roadbook, ni
 * l'extrapolation entre deux relevés ne sont possibles.
 *
 * Deux principes structurent le fichier :
 *
 *  - **Priorité aux traces.** Une trace chargée décrit le parcours voulu ; le
 *    réseau routier n'est qu'un repli quand on est hors trace.
 *  - **Hystérésis asymétrique.** On s'accroche à 20 m mais on ne décroche qu'à
 *    25 m, et cette tolérance ne vaut que pour la cible déjà accrochée : passer
 *    d'une trace à une autre exige de nouveau les 20 m stricts.
 */

import {
  bboxMargins,
  computeBearing,
  haversine,
  lowerBound,
  projectOnSegment,
  type LatLng,
} from './geo'
import type { GpxTrack } from './track'

/** Distance maximale pour s'accrocher à une cible non encore suivie. */
export const ATTACH_THRESHOLD_M = 20.0

/** Distance au-delà de laquelle on lâche la cible suivie. */
export const RELEASE_THRESHOLD_M = 25.0

/** Rayon de la requête sur le réseau routier, en pixels écran. */
export const ROAD_QUERY_RADIUS_PX = 50

/**
 * Couches routières interrogées, pour les deux schémas de tuiles utilisés.
 *
 * MapLibre GL JS **lève une erreur** sur une couche inconnue, contrairement au
 * SDK Android qui les ignore : la liste doit être filtrée contre le style
 * courant avant toute requête (voir `createMapRoadQuery`).
 */
export const ROAD_LAYERS: readonly string[] = [
  // OpenFreeMap — schéma OpenMapTiles
  'road_motorway',
  'road_trunk_primary',
  'road_secondary_tertiary',
  'road_minor',
  'road_link',
  'road_service_track',
  'road_path_pedestrian',
  'bridge_motorway',
  'bridge_trunk_primary',
  'bridge_secondary_tertiary',
  'bridge_minor',
  'bridge_service_track',
  'tunnel_motorway',
  'tunnel_trunk_primary',
  'tunnel_secondary_tertiary',
  'tunnel_minor',
  'tunnel_service_track',

  // VersaTiles — schéma Shortbread
  'street-motorway',
  'street-trunk',
  'street-primary',
  'street-secondary',
  'street-tertiary',
  'street-residential',
  'street-unclassified',
  'street-service',
  'street-livingstreet',
  'street-track',
  'street-pedestrian',
  'bridge-street-motorway',
  'bridge-street-trunk',
  'bridge-street-primary',
  'bridge-street-secondary',
  'bridge-street-tertiary',
  'bridge-street-residential',
  'bridge-street-unclassified',
  'bridge-street-service',
  'tunnel-street-motorway',
  'tunnel-street-trunk',
  'tunnel-street-primary',
  'tunnel-street-secondary',
  'tunnel-street-tertiary',
  'tunnel-street-residential',
  'tunnel-street-unclassified',
  'tunnel-street-service',
] as const

export interface SnapInfo {
  readonly lat: number
  readonly lon: number
  /** Abscisse curviligne le long de la trace, en mètres ; -1 sur une route. */
  readonly curvilinearDistance: number
  /** Cap du segment accroché, en degrés. */
  readonly segmentBearing: number
  readonly track: GpxTrack | null
  /** `true` pour une trace GPX, `false` pour le réseau routier. */
  readonly isTrackSnap: boolean
}

interface SnapCandidate {
  lat: number
  lon: number
  distance: number
  curvilinearDistance: number
  segmentBearing: number
  track: GpxTrack | null
  trackId: string | null
}

/**
 * Cible actuellement suivie.
 *
 * Distinguer « la trace A » de « la route » est indispensable : l'hystérésis
 * de maintien ne doit s'appliquer qu'à la cible en cours.
 */
type SnapTarget = { kind: 'track'; trackId: string } | { kind: 'road' }

function sameTarget(a: SnapTarget | null, b: SnapTarget): boolean {
  if (a === null) return false
  if (a.kind !== b.kind) return false
  return a.kind === 'road' || a.trackId === (b as { trackId: string }).trackId
}

/**
 * Fournit les polylignes du réseau routier autour d'une position.
 *
 * Abstraite pour que la logique d'accrochage reste testable sans carte, et
 * pour pouvoir la désactiver quand le style n'expose aucune couche routière.
 */
export type RoadQuery = (lat: number, lon: number) => readonly (readonly LatLng[])[]

export class SnapToRoad {
  #currentTarget: SnapTarget | null = null

  constructor(
    private readonly getVisibleTracks: () => readonly GpxTrack[],
    private readonly queryRoads: RoadQuery | null = null,
  ) {}

  /** Cible suivie, exposée pour le diagnostic et les tests. */
  get currentTargetId(): string | null {
    if (this.#currentTarget === null) return null
    return this.#currentTarget.kind === 'road' ? 'road' : this.#currentTarget.trackId
  }

  /**
   * Calcule la position accrochée.
   *
   * `lastCurvilinearDistance` lève l'ambiguïté sur les boucles : quand deux
   * passages de la même trace sont à distance comparable, on retient celui qui
   * prolonge la progression connue.
   *
   * @returns `null` si rien n'est à portée — on affiche alors le relevé brut.
   */
  computeSnappedPosition(
    lat: number,
    lon: number,
    lastCurvilinearDistance = -1,
  ): SnapInfo | null {
    const trackCandidate = this.#findNearestTrackPoint(
      lat,
      lon,
      lastCurvilinearDistance,
    )

    if (trackCandidate !== null) {
      const info = this.#handleCandidate(trackCandidate, true)
      if (info !== null) return info
      // Pas de retour anticipé : un candidat resté dans la bande 20-25 m sans
      // parvenir à s'accrocher ne doit pas masquer le repli routier.
    }

    const roadCandidate = this.#findNearestRoadPoint(lat, lon)
    if (roadCandidate !== null) {
      const info = this.#handleCandidate(roadCandidate, false)
      if (info !== null) return info
    }

    this.#currentTarget = null
    return null
  }

  /** Libère la cible si elle désigne une trace qui vient d'être retirée. */
  releaseTrack(trackId: string): void {
    if (
      this.#currentTarget?.kind === 'track' &&
      this.#currentTarget.trackId === trackId
    ) {
      this.#currentTarget = null
    }
  }

  reset(): void {
    this.#currentTarget = null
  }

  /**
   * Applique l'hystérésis et met à jour la cible suivie.
   *
   * Sur un refus d'attachement, la cible courante est laissée **inchangée** :
   * un candidat trop lointain ne doit pas décrocher ce qui est déjà suivi.
   */
  #handleCandidate(candidate: SnapCandidate, isTrack: boolean): SnapInfo | null {
    const newTarget: SnapTarget =
      isTrack && candidate.trackId !== null
        ? { kind: 'track', trackId: candidate.trackId }
        : { kind: 'road' }

    if (sameTarget(this.#currentTarget, newTarget)) {
      if (candidate.distance > RELEASE_THRESHOLD_M) {
        this.#currentTarget = null
        return null
      }
    } else {
      if (candidate.distance > ATTACH_THRESHOLD_M) {
        return null
      }
      this.#currentTarget = newTarget
    }

    return {
      lat: candidate.lat,
      lon: candidate.lon,
      curvilinearDistance: candidate.curvilinearDistance,
      segmentBearing: candidate.segmentBearing,
      track: candidate.track,
      isTrackSnap: isTrack,
    }
  }

  /**
   * Meilleur candidat parmi les traces visibles, dans l'ordre du tiroir.
   *
   * Volontairement distincte de `GpxTrack.distanceAtPoint` : celle-ci répond à
   * « sur quoi roule-t-on ? » et compare plusieurs traces, l'autre à « où
   * l'utilisateur a-t-il touché ? » sur une trace donnée. Les fusionner ferait
   * perdre l'un des deux contrats.
   */
  #findNearestTrackPoint(
    posLat: number,
    posLon: number,
    lastCurvilinearDistance: number,
  ): SnapCandidate | null {
    // Initialisé au seuil de décrochage pour que la bande 20-25 m reste
    // visible : c'est l'hystérésis qui tranchera ensuite.
    let bestDistance = RELEASE_THRESHOLD_M
    let bestLat = 0
    let bestLon = 0
    let bestCurvilinear = 0
    let bestBearing = 0
    let bestTrack: GpxTrack | null = null
    let bestTrackId: string | null = null

    const { latMargin, lonMargin } = bboxMargins(posLat, RELEASE_THRESHOLD_M)

    for (const track of this.getVisibleTracks()) {
      if (!track.visible) continue

      const cumulative = track.cumulativeDistances
      let flatBase = 0

      for (const segment of track.segments) {
        const points = segment.points

        if (points.length < 2) {
          // L'index à plat avance quand même : il indexe les distances
          // cumulées, qui comptent tous les points.
          flatBase += points.length
          continue
        }

        for (let i = 0; i < points.length - 1; i++) {
          const p1 = points[i]!
          const p2 = points[i + 1]!

          const minLat = Math.min(p1.lat, p2.lat)
          const maxLat = Math.max(p1.lat, p2.lat)
          if (posLat < minLat - latMargin || posLat > maxLat + latMargin) continue

          const minLon = Math.min(p1.lon, p2.lon)
          const maxLon = Math.max(p1.lon, p2.lon)
          if (posLon < minLon - lonMargin || posLon > maxLon + lonMargin) continue

          const projection = projectOnSegment(
            posLat,
            posLon,
            p1.lat,
            p1.lon,
            p2.lat,
            p2.lon,
          )
          const distance = haversine(
            posLat,
            posLon,
            projection.lat,
            projection.lon,
          )

          const segmentStart = cumulative[flatBase + i]!
          const candidateCurvilinear =
            segmentStart +
            projection.t * (cumulative[flatBase + i + 1]! - segmentStart)

          const dominated = isDominated({
            distance,
            bestDistance,
            candidateCurvilinear,
            bestCurvilinear,
            lastCurvilinearDistance,
            // Le départage par progression n'a de sens qu'entre passages
            // d'une même trace : comparer des abscisses curvilignes de deux
            // traces différentes n'aurait aucune signification.
            sameTrack: bestTrackId === track.id,
          })

          if (!dominated) {
            bestDistance = distance
            bestLat = projection.lat
            bestLon = projection.lon
            bestTrackId = track.id
            bestTrack = track
            bestCurvilinear = candidateCurvilinear
            bestBearing = computeBearing(p1.lat, p1.lon, p2.lat, p2.lon)
          }
        }

        flatBase += points.length
      }

      // Sortie anticipée : une trace prioritaire clairement accrochée dispense
      // de parcourir les suivantes.
      if (bestTrackId !== null && bestDistance < ATTACH_THRESHOLD_M) {
        break
      }
    }

    if (bestTrackId === null) return null

    return {
      lat: bestLat,
      lon: bestLon,
      distance: bestDistance,
      curvilinearDistance: bestCurvilinear,
      segmentBearing: bestBearing,
      track: bestTrack,
      trackId: bestTrackId,
    }
  }

  /**
   * Meilleur candidat sur le réseau routier.
   *
   * Aucune abscisse curviligne n'est produite : le réseau n'a pas d'origine de
   * parcours, l'extrapolation se fera en ligne droite.
   */
  #findNearestRoadPoint(posLat: number, posLon: number): SnapCandidate | null {
    if (this.queryRoads === null) return null

    let bestDistance = RELEASE_THRESHOLD_M
    let bestLat = 0
    let bestLon = 0
    let bestBearing = 0
    let found = false

    for (const line of this.queryRoads(posLat, posLon)) {
      for (let i = 0; i < line.length - 1; i++) {
        const p1 = line[i]!
        const p2 = line[i + 1]!

        const projection = projectOnSegment(
          posLat,
          posLon,
          p1.lat,
          p1.lon,
          p2.lat,
          p2.lon,
        )
        const distance = haversine(posLat, posLon, projection.lat, projection.lon)

        if (distance < bestDistance) {
          bestDistance = distance
          bestLat = projection.lat
          bestLon = projection.lon
          bestBearing = computeBearing(p1.lat, p1.lon, p2.lat, p2.lon)
          found = true
        }
      }
    }

    if (!found) return null

    return {
      lat: bestLat,
      lon: bestLon,
      distance: bestDistance,
      curvilinearDistance: -1,
      segmentBearing: bestBearing,
      track: null,
      trackId: null,
    }
  }
}

/**
 * Décide si un candidat est écarté au profit du meilleur courant.
 *
 * Hors désambiguïsation de boucle, la règle est la simple distance
 * géographique. Sur une même trace et avec une progression connue, deux
 * candidats à moins d'un mètre l'un de l'autre sont départagés par leur
 * proximité à cette progression — sans quoi un aller-retour ferait sauter la
 * position d'un passage à l'autre à chaque relevé.
 */
function isDominated(input: {
  distance: number
  bestDistance: number
  candidateCurvilinear: number
  bestCurvilinear: number
  lastCurvilinearDistance: number
  sameTrack: boolean
}): boolean {
  const {
    distance,
    bestDistance,
    candidateCurvilinear,
    bestCurvilinear,
    lastCurvilinearDistance,
    sameTrack,
  } = input

  const useHint =
    lastCurvilinearDistance >= 0 && distance < RELEASE_THRESHOLD_M && sameTrack

  if (!useHint) {
    return distance >= bestDistance
  }

  const diff = distance - bestDistance

  // Nettement plus proche : la géographie tranche seule.
  if (diff < -1.0) return false

  // À égalité near-metrique : c'est la progression qui départage.
  if (diff <= 1.0) {
    return (
      Math.abs(candidateCurvilinear - lastCurvilinearDistance) >=
      Math.abs(bestCurvilinear - lastCurvilinearDistance)
    )
  }

  return true
}

export interface DeadReckonResult {
  readonly lat: number
  readonly lon: number
  readonly bearing: number
}

/**
 * Position et cap à une abscisse curviligne donnée.
 *
 * Sert à l'extrapolation entre deux relevés GPS : plutôt que de figer le
 * curseur pendant une seconde, on le fait avancer le long de la trace à la
 * vitesse mesurée, ce qui donne un déplacement continu à l'écran.
 */
export function positionBearingAtDistance(
  track: GpxTrack,
  distance: number,
): DeadReckonResult | null {
  if (distance < 0) return null

  const points = track.allPoints
  if (points.length === 0) return null

  const cumulative = track.cumulativeDistances
  const lastIndex = points.length - 1

  if (points.length === 1 || distance >= cumulative[lastIndex]!) {
    const last = points[lastIndex]!
    const previous = points.length >= 2 ? points[lastIndex - 1]! : null

    return {
      lat: last.lat,
      lon: last.lon,
      bearing:
        previous === null
          ? 0
          : computeBearing(previous.lat, previous.lon, last.lat, last.lon),
    }
  }

  const j = Math.max(lowerBound(cumulative, distance), 1)
  const p1 = points[j - 1]!
  const p2 = points[j]!

  const span = cumulative[j]! - cumulative[j - 1]!
  const t = span > 0 ? (distance - cumulative[j - 1]!) / span : 0

  return {
    lat: p1.lat + t * (p2.lat - p1.lat),
    lon: p1.lon + t * (p2.lon - p1.lon),
    bearing: computeBearing(p1.lat, p1.lon, p2.lat, p2.lon),
  }
}
